import { useEffect, useRef, useState } from 'react';
import { Plus, Tag, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TagMultiInputProps {
  selected: string[];
  suggestions: string[];
  onChange: (tags: string[]) => void;
  onCreateTag?: (tag: string) => void;
  disabled?: boolean;
  placeholder?: string;
  createLabel?: (tag: string) => string;
  className?: string;
  inputClassName?: string;
}

export function TagMultiInput({
  selected,
  suggestions,
  onChange,
  onCreateTag,
  disabled = false,
  placeholder = 'Ajouter des tags…',
  createLabel = (tag) => `Créer « ${tag} »`,
  className,
  inputClassName,
}: TagMultiInputProps) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const normalizedSuggestions = suggestions.filter(Boolean);
  const selectedKeys = new Set(selected.map((t) => t.toLowerCase()));
  const filtered = normalizedSuggestions.filter(
    (tag) =>
      tag.toLowerCase().includes(input.toLowerCase()) && !selectedKeys.has(tag.toLowerCase()),
  );

  const trimmed = input.trim();
  const canCreate =
    trimmed.length > 0
    && !normalizedSuggestions.some((tag) => tag.toLowerCase() === trimmed.toLowerCase())
    && !selectedKeys.has(trimmed.toLowerCase());

  const addTag = (tag: string) => {
    const value = tag.trim();
    if (!value || selectedKeys.has(value.toLowerCase())) return;
    if (!normalizedSuggestions.some((t) => t.toLowerCase() === value.toLowerCase())) {
      onCreateTag?.(value);
    }
    onChange([...selected, value]);
    setInput('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const removeTag = (tag: string) => onChange(selected.filter((t) => t !== tag));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0 && !canCreate) addTag(filtered[0]);
      else if (trimmed) addTag(trimmed);
    }
    if (e.key === 'Backspace' && !input && selected.length > 0) {
      removeTag(selected[selected.length - 1]);
    }
    if (e.key === 'Escape') setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex min-h-[42px] flex-wrap gap-1.5 rounded-xl border px-3 py-2 transition-colors',
          'border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)]',
          'focus-within:border-[color:var(--inst-player-primary,#0C1C38)] focus-within:ring-2 focus-within:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)]',
          'dark:border-gray-600 dark:bg-gray-900',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-text',
          inputClassName,
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {selected.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)] px-2 py-0.5 text-xs font-medium text-[color:var(--inst-player-primary,#0C1C38)] dark:text-emerald-200"
          >
            <Tag className="h-3 w-3 shrink-0" aria-hidden />
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                aria-label={`Retirer ${tag}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          disabled={disabled}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="min-w-[120px] flex-1 bg-transparent text-sm text-[color:var(--inst-player-text,#111827)] placeholder:text-[color:var(--inst-player-text-muted,#6b7280)] outline-none dark:text-gray-100"
        />
      </div>

      {open && !disabled && (filtered.length > 0 || canCreate) && (
        <div className="absolute start-0 end-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-xl border border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {filtered.map((tag) => (
            <button
              key={tag}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-[color:var(--inst-player-text,#111827)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,transparent)] dark:text-gray-200"
            >
              <Tag className="h-3.5 w-3.5 shrink-0 text-[color:var(--inst-player-primary,#0C1C38)] dark:text-emerald-300" aria-hidden />
              {tag}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addTag(trimmed); }}
              className="flex w-full items-center gap-2 border-t border-[color:var(--inst-player-border,#e5e7eb)] px-3 py-2 text-start text-sm font-medium text-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,transparent)] dark:border-gray-700 dark:text-emerald-300"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {createLabel(trimmed)}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
